import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import EditOfferModal from '../../components/admin/EditOfferModal';
import styles from './AdminOffers.module.css';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [type, setType] = useState('Crear');

  useEffect(() => {
    loadOffers();
    loadCategories();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiService.getActiveOffers();
      setOffers(res.products || []);
    } catch (err) {
      console.error('Error cargando ofertas:', err);
      setError('No se pudieron cargar las ofertas');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.getCategories();
      const categoriesArray = Array.isArray(data) ? data : (data?.categories || []);
      setCategories(categoriesArray);
    } catch {
      setCategories([]);
    }
  };

  const categoryNameById = useMemo(() => {
    const map = {};
    for (const category of categories) {
      if (category?.id == null) continue;
      map[String(category.id)] = category?.name || '';
    }
    return map;
  }, [categories]);

  const handleCreateOffer = () => {
    setSelectedOffer(null);
    setShowModal(true);
    setType('Crear');
  };

  const handleEditOffer = (offer) => {
    setSelectedOffer(offer);
    setShowModal(true);
    setType('Editar');
  };

  const handleSaveOffer = async (formData) => {
    try {
      setLoading(true);
      await apiService.saveOffer({
        product_id: formData.product_id, // Mantener como UUID string
        is_featured: formData.is_featured,
        is_prime: formData.is_prime,
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
      });
      setShowModal(false);
      setSelectedOffer(null);
      await loadOffers();
    } catch (err) {
      console.error('Error guardando oferta:', err);
      setError('No se pudo guardar la oferta');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOffer = async (productId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta oferta?')) {
      try {
        setLoading(true);
        await apiService.deleteOffer(productId);
        await loadOffers();
      } catch (err) {
        console.error('Error eliminando oferta:', err);
        setError('No se pudo eliminar la oferta');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Gestionar Ofertas</h1>
          <button
            className={styles.createBtn}
            onClick={handleCreateOffer}
            disabled={loading}
          >
            + Crear Oferta
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.content}>
          {loading && offers.length === 0 ? (
            <div className={styles.loading}>Cargando ofertas...</div>
          ) : offers.length === 0 ? (
            <div className={styles.empty}>
              <p>No hay ofertas activas</p>
              <button
                className={styles.createBtnAlt}
                onClick={handleCreateOffer}
              >
                Crear tu primera oferta
              </button>
            </div>
          ) : (
            <div className={styles.offersGrid}>
              {offers.map(offer => (
                <div key={offer.id} className={styles.offerCard}>
                  <div className={styles.cardHeader}>
                    <h3>{offer.name}</h3>
                    <div className={styles.badges}>
                      {offer.is_featured && <span className={styles.badgeFeatured}>Destacado</span>}
                      {offer.is_prime && <span className={styles.badgePrime}>Prime</span>}
                    </div>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.prices}>
                      <div className={styles.priceItem}>
                        <label>Precio Actual:</label>
                        <span className={styles.price}>${offer.price}</span>
                      </div>
                      {offer.original_price && (
                        <div className={styles.priceItem}>
                          <label>Precio Original:</label>
                          <span className={styles.priceOriginal}>${offer.original_price}</span>
                        </div>
                      )}
                      {offer.cost_price && (
                        <div className={styles.priceItem}>
                          <label>Precio de Costo:</label>
                          <span className={styles.priceCost}>${offer.cost_price}</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.info}>
                      <p><strong>Stock:</strong> {offer.stock_quantity} unidades</p>
                      <p><strong>Categoría:</strong> {categoryNameById[String(offer.category_id)] || '-'}</p>
                    </div>
                  </div>

                  <div className={styles.cardFooter}>
                    <button
                      className={styles.editBtn}
                      onClick={() => handleEditOffer(offer)}
                      disabled={loading}
                    >
                      Editar
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDeleteOffer(offer.id)}
                      disabled={loading}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <EditOfferModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedOffer(null);
          }}
          offer={selectedOffer}
          onSave={handleSaveOffer}
          type={type}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminOffers;
